import React, { FC, Fragment } from 'react';

import {
  IRating,
  IRatingItems,
  IRatingsData,
  Language,
  language,
} from '@pharmaplan/common';

import { Box } from '@mui/material';
import { useAppSelector } from '../../../../../hooks/useAppSelector';
import AllLabelRatings from '../../../../Admin/DetailedRatings/AllRatings/AllLabelRatings';
import useAdmin from '../../../../../hooks/useAdmin';
import CustomProgressBar from '../../../../common/CustomProgressBar';

const { en } = Language;

interface IViewPharmacistRatingContent {
  ratingData: IRatingItems;
  ratingDetails: IRating;
  idProperty: keyof IRatingsData;
  itemProperty: keyof Omit<
    IRatingItems,
    'favorite' | 'displayDetailForm' | 'professionalRatingQuestions'
  >;
}

const ViewPharmacistRatingContent: FC<IViewPharmacistRatingContent> = ({
  ratingData,
  ratingDetails,
  idProperty,
  itemProperty,
}) => {
  const userLanguage = useAppSelector(language);
  const { isAdmin } = useAdmin();

  const isEN = userLanguage === en;

  const ratingItems = ratingData?.[itemProperty] ?? [];
  const { ratings } = ratingDetails ?? {};

  const adminContainer = isAdmin ? { padding: '32px' } : {};

  const tempObj = ratings.reduce((acc: Record<string, number>, cur) => {
    acc[cur?.[idProperty] ?? ''] = parseFloat(cur?.rating ?? 0);
    return acc;
  }, {});

  const renderRating = (
    primaryText: string,
    secondaryText: number,
    progress: number,
  ) => {
    const handleSecondary = `${secondaryText}%`;

    if (isAdmin) {
      return (
        <AllLabelRatings
          value={progress}
          percentage={secondaryText}
          label={primaryText}
        />
      );
    }

    return (
      <CustomProgressBar
        progress={progress}
        primaryText={primaryText}
        secondaryText={handleSecondary}
      />
    );
  };

  return (
    <Box sx={adminContainer}>
      {ratingItems?.map((item) => {
        const { englishName, frenchName } = item ?? {};
        const id = item?.[idProperty as unknown as keyof typeof item];
        const primaryText = isEN ? englishName : frenchName;
        const secondaryText = tempObj[id] ?? 0;
        const progress = tempObj[id] ?? 0;

        return (
          <Fragment key={id}>
            {renderRating(primaryText, secondaryText, progress)}
          </Fragment>
        );
      })}
    </Box>
  );
};

export default ViewPharmacistRatingContent;
